import React, { useEffect, useState } from 'react';
import {
  AppLayout,
  SpaceBetween,
  Container,
  Header,
  Box,
  Button,
  Alert,
  ColumnLayout,
} from '@cloudscape-design/components';
import TopPanel from '../TopPanel';
import ServiceNavigation from '../ServiceNavigation.jsx';
import SynopsisPanelV3 from '../SynopsisPanelV3';
import ExtendingPanel from '../ExtendingPanel.jsx';
import { sendAnalytics } from '../../resources/rum-provider';
import { EDGE_EXPERIENCE_CENTER_ENDPOINT } from '../../resources/prod-env.jsx';
import architectureImg from './images/architecture.png';

const demoModule = "edge-experience-center";

// The customer view is served by this demo's own stack (backend/edge-experience-center):
// private S3 + OAC behind its own CloudFront distribution, under /customer/.
// deploy.sh writes the base URL into prod-env.jsx; empty means not deployed yet.
const base = (EDGE_EXPERIENCE_CENTER_ENDPOINT || '').replace(/\/+$/, '');
const src = base ? `${base}/customer/index.html` : '';

export default function EdgeExperienceCenter() {
  // Start with the side nav collapsed so the tool gets the full width; the menu button reopens it.
  const [navOpen, setNavOpen] = useState(false);
  // Usage ping on mount - the demo was opened.
  useEffect(() => {
    sendAnalytics({ demo: demoModule, view: 'customer' });
  }, []);

  const openFullScreen = () => {
    sendAnalytics({ demo: demoModule, action: 'open-full-screen', view: 'customer' });
    window.open(src, '_blank', 'noopener,noreferrer');
  };

  return (
    <React.Fragment>
      <TopPanel label="Edge Experience Center" />
      <AppLayout
        navigation={<ServiceNavigation />}
        navigationOpen={navOpen}
        onNavigationChange={({ detail }) => setNavOpen(detail.open)}
        maxContentWidth={Number.MAX_VALUE}
        toolsHide
        content={
          <SpaceBetween size="m">
            <SynopsisPanelV3 label="Synopsis" demotype="demo" architectureImgs={[architectureImg]}>
              An interactive walkthrough of protecting web applications at the edge with{' '}
              <b>AWS WAF</b> and <b>CloudFront</b>: the bot-threat story, an animated threat-and-defense
              view, a step-by-step walkthrough in the real AWS WAF console (with video), a Web ACL
              designer, a hands-on "you are the WAF" lab, AI-traffic monetization, and an edge plan
              and pilot plan. This is the customer view: it contains no competitor content, internal
              guides or seller talk-tracks, so it is safe to screen-share and to send as a link. A field
              (internal) view is planned for a later release, behind Midway sign-in.
            </SynopsisPanelV3>

            <Container
              header={
                <Header
                  variant="h2"
                  description="Customer view: safe to screen-share with customers."
                  actions={
                    <Button iconName="external" iconAlign="right" disabled={!src} onClick={openFullScreen}>
                      Open full screen
                    </Button>
                  }
                >
                  Edge Experience Center
                </Header>
              }
            >
              {src ? (
                <iframe
                  src={src}
                  title="Edge Experience Center - Customer view"
                  allow="clipboard-read; clipboard-write; fullscreen"
                  allowFullScreen
                  style={{ display: 'block', width: '100%', height: 'calc(100vh - 180px)', minHeight: 720, border: 'none' }}
                />
              ) : (
                <Alert type="info" header="Not deployed yet">
                  Run <code>backend/edge-experience-center/deploy.sh</code> to publish the customer view;
                  it writes <code>EDGE_EXPERIENCE_CENTER_ENDPOINT</code> into prod-env.jsx.
                </Alert>
              )}
            </Container>

            <ExtendingPanel label="Extend it">
              <ColumnLayout columns={2} variant='text-grid' className='extend_it'>
                <Box>Questions/ideas on this demo?</Box>
                <Box variant='strong'>
                  Mark Levitan, mlevita@amazon.com
                </Box>
              </ColumnLayout>
            </ExtendingPanel>
          </SpaceBetween>
        }
        contentType="default"
      />
    </React.Fragment>
  );
}
